import { Mutex } from 'async-mutex';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { environment } from 'src/environments/environment';
import { AuthenticationService, IAMService, UIService } from '../../services.module';
import { APIQueryParamFragments } from 'server-model';

// Custom header to skip auth validation
export const SKIP_AUTH_HEADER = 'x-skip-auth-validation';

export interface RequestMetadata {
    skipAuth?: boolean;
    skipDuplicateCheck?: boolean;
    timeout?: number;
    retryCount?: number;
}

export class AppServerConnector {
    static readonly authTokenMutex = new Mutex();
    protected axios!: AxiosInstance;

    // ==== CONFIG ====
    private CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

    // inside AppServerConnector
    private responseCache = new Map<string, { expiry: number; response: AxiosResponse }>();

    // Map to store pending request promises
    private pendingRequests = new Map<string, Promise<AxiosResponse>>();
    // Flag to track ongoing token refresh
    private isRefreshingToken = false;
    // Queue of requests waiting for token refresh
    private tokenRefreshQueue: Array<{
        resolve: (value: any) => void;
        reject: (reason: any) => void;
    }> = [];

    constructor(
        protected iamService: IAMService,
        protected authenticationService: AuthenticationService,
        protected uiService: UIService,
    ) {
        this.createAxiosInstance();
    }

    private createAxiosInstance() {
        this.axios = axios.create({
            baseURL: `${environment.appServerUrl}`,
            timeout: 30000, // 30 seconds default timeout
        });
        this.addAxiosRequestInterceptors();
        this.addAxiosResponseInterceptors();
    }

    private addAxiosRequestInterceptors() {
        this.axios.interceptors.request.use(
            async (req: AxiosRequestConfig) => {
                return (await this.requestInterceptor(req)) as any;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    private async requestInterceptor(req: AxiosRequestConfig) {
        const release = await AppServerConnector.authTokenMutex.acquire();
        try {
            // Check if auth should be skipped
            const skipAuth = req.headers?.[SKIP_AUTH_HEADER] === 'true';
            const skipDuplicateCheck = req.headers?.['x-skip-duplicate-check'] === 'true';

            // Clean up custom headers so they don't go to server
            if (req.headers) {
                delete req.headers[SKIP_AUTH_HEADER];
                delete req.headers['x-skip-duplicate-check'];
            }

            // Always append auth params (user ID, org ID, etc.) even if skipping token validation
            this.appendAuthParams(req);

            // Handle duplicate request check (unless skipped)
            if (!skipDuplicateCheck) {
                const requestIdentifier = this.getRequestIdentifier(req);
                const pendingRequest = this.pendingRequests.get(requestIdentifier);
                if (pendingRequest) {
                    return Promise.reject({
                        isPendingRequest: true,
                        pendingPromise: pendingRequest
                    });
                }
            }

            // Handle authentication (unless skipped)
            if (!skipAuth) {
                const isValidToken = await this.validateAuthToken();
                if (!isValidToken) {
                    return Promise.reject(new Error('Invalid auth token'));
                }

                // Set authorization header
                if (!req.headers) {
                    req.headers = {};
                }
                req.headers.Authorization = `Bearer ${this.iamService.getAuthToken()}`;
            }

            // Handle duplicate tracking (unless skipped)
            if (!skipDuplicateCheck) {
                const requestIdentifier = this.getRequestIdentifier(req);
                const requestPromise = this.axios(req)
                    .then((response) => {
                        this.pendingRequests.delete(requestIdentifier);
                        return response;
                    })
                    .catch((error) => {
                        this.pendingRequests.delete(requestIdentifier);
                        throw error;
                    });

                this.pendingRequests.set(requestIdentifier, requestPromise);
            }

            return req;
        } catch (error: any) {
            if (error.isPendingRequest) {
                return error.pendingPromise;
            }
            console.log("Request interceptor error:", error);
            throw error;
        } finally {
            release();
        }
    }

    // Enhanced method with metadata support
    protected async makeRequest<T = any>(
        config: AxiosRequestConfig,
        metadata?: RequestMetadata
    ): Promise<AxiosResponse<T>> {
        // Apply metadata to request config
        if (metadata) {
            if (!config.headers) config.headers = {};

            if (metadata.skipAuth) {
                config.headers[SKIP_AUTH_HEADER] = 'true';
            }

            if (metadata.skipDuplicateCheck) {
                config.headers['x-skip-duplicate-check'] = 'true';
            }

            if (metadata.timeout) {
                config.timeout = metadata.timeout;
            }
        }

        return this.getCachedOrFetch(config);
    }

    private async getCachedOrFetch(config: AxiosRequestConfig): Promise<AxiosResponse> {
        // Only cache GET requests
        if (config.method?.toUpperCase() !== 'GET') {
            return this.axios(config);
        }

        const key = this.getRequestIdentifier(config);
        const now = Date.now();

        // Serve from cache if valid
        const cached = this.responseCache.get(key);
        if (cached && cached.expiry > now) {
            return Promise.resolve(cached.response);
        }

        // Otherwise fetch and store
        const response = await this.axios(config);
        this.responseCache.set(key, {
            expiry: now + this.CACHE_TTL_MS,
            response: response,
        });
        return response;
    }


    // Convenience methods for different HTTP verbs with metadata support
    protected async get<T = any>(url: string, config?: AxiosRequestConfig, metadata?: RequestMetadata): Promise<AxiosResponse<T>> {
        return this.makeRequest<T>({ ...config, method: 'GET', url }, metadata);
    }

    protected async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig, metadata?: RequestMetadata): Promise<AxiosResponse<T>> {
        return this.makeRequest<T>({ ...config, method: 'POST', url, data }, metadata);
    }

    protected async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig, metadata?: RequestMetadata): Promise<AxiosResponse<T>> {
        return this.makeRequest<T>({ ...config, method: 'PUT', url, data }, metadata);
    }

    protected async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig, metadata?: RequestMetadata): Promise<AxiosResponse<T>> {
        return this.makeRequest<T>({ ...config, method: 'PATCH', url, data }, metadata);
    }

    protected async delete<T = any>(url: string, config?: AxiosRequestConfig, metadata?: RequestMetadata): Promise<AxiosResponse<T>> {
        return this.makeRequest<T>({ ...config, method: 'DELETE', url }, metadata);
    }

    // Method to handle toast display and reload
    toastActionButtonClick() {
        this.uiService.reload();
    }

    private async validateAuthToken(): Promise<boolean> {
        if (!this.iamService.checkAuthTokenValidity()) {
            return await this.refreshTokenSafely();
        }
        return true;
    }

    // Enhanced token refresh with better error handling
    private async refreshTokenSafely(): Promise<boolean> {
        if (this.isRefreshingToken) {
            return new Promise<boolean>((resolve, reject) => {
                this.tokenRefreshQueue.push({ resolve, reject });
            });
        }

        this.isRefreshingToken = true;

        try {
            const isTokenAvailable = await this.iamService.refreshToken();
            this.processTokenRefreshQueue(isTokenAvailable);
            return isTokenAvailable;
        } catch (error) {
            console.error("Token refresh error:", error);
            this.processTokenRefreshQueue(false);
            return false;
        } finally {
            this.isRefreshingToken = false;
        }
    }

    private processTokenRefreshQueue(success: boolean) {
        this.tokenRefreshQueue.forEach(({ resolve, reject }) => {
            if (success) {
                resolve(success);
            } else {
                reject(new Error('Token refresh failed'));
            }
        });
        this.tokenRefreshQueue = [];
    }

    private appendAuthParams(req: AxiosRequestConfig) {
        const userId = this.iamService.getUserId();
        const organisationId = this.iamService.getOrganisationId();
        const appVersion = environment.appVersion;

        // Handle both URL params and query params
        if (req.url && req.url.includes('?')) {
            const urlParts = req.url.split('?');
            const existingParams = new URLSearchParams(urlParts[1]);

            if (userId) existingParams.set(APIQueryParamFragments.USER_ID, userId);
            if (organisationId) existingParams.set(APIQueryParamFragments.ORGANISATION_ID, organisationId);
            existingParams.set(APIQueryParamFragments.APP_VERSION, appVersion);

            req.url = `${urlParts[0]}?${existingParams.toString()}`;
        } else {
            // Use params object
            const currentParams = new URLSearchParams(req.params as any || {});
            if (userId) currentParams.set(APIQueryParamFragments.USER_ID, userId);
            if (organisationId) currentParams.set(APIQueryParamFragments.ORGANISATION_ID, organisationId);
            currentParams.set(APIQueryParamFragments.APP_VERSION, appVersion);
            req.params = currentParams;
        }
    }

    private addAxiosResponseInterceptors() {
        this.axios.interceptors.response.use(
            (res: AxiosResponse) => {
                return res;
            },
            async (error) => {
                return await this.handleResponseError(error);
            }
        );
    }

    private async handleResponseError(error: any) {
        if (!error.response) {
            // Network error or request was cancelled
            return Promise.reject(error);
        }

        const status = error.response.status;

        // Handle different error types
        switch (status) {
            case 401:
                return await this.handle401Error(error);
            case 403:
                return await this.handle403Error(error);
            case 429:
                return await this.handle429Error(error);
            case 500:
            case 502:
            case 503:
            case 504:
                return await this.handle5xxError(error);
            default:
                return Promise.reject(error);
        }
    }

    private async handle401Error(error: any) {
        try {
            const release = await AppServerConnector.authTokenMutex.acquire();

            try {
                const isTokenRefreshed = await this.refreshTokenSafely();

                if (isTokenRefreshed) {
                    const originalRequest = error.config;
                    if (!originalRequest.headers) {
                        originalRequest.headers = {};
                    }
                    originalRequest.headers.Authorization = `Bearer ${this.iamService.getAuthToken()}`;

                    // Prevent infinite retry loops
                    if (!originalRequest._retry) {
                        originalRequest._retry = true;
                        return this.axios(originalRequest);
                    }
                }

                const isCriticalEndpoint = this.isCriticalAuthEndpoint(error.config.url);
                if (isCriticalEndpoint) {
                    this.logout("Your session has expired. Please log in again.");
                }

                return Promise.reject(error);
            } finally {
                release();
            }
        } catch (refreshError) {
            console.error("Error during token refresh after 401:", refreshError);
            this.logout("Authentication error. Please log in again.");
            return Promise.reject(error);
        }
    }

    private async handle403Error(error: any) {
        console.warn("Access forbidden:", error.config.url);
        // You might want to show a specific message for forbidden access
        return Promise.reject(error);
    }

    private async handle429Error(error: any) {
        // Rate limiting - could implement retry with backoff
        const retryAfter = error.response.headers['retry-after'] || 1;
        console.warn(`Rate limited. Retry after ${retryAfter} seconds`);

        // Optional: Implement automatic retry with backoff
        // await this.delay(retryAfter * 1000);
        // return this.axios(error.config);

        return Promise.reject(error);
    }

    private async handle5xxError(error: any) {
        // Server errors - could implement retry logic
        console.error("Server error:", error.response.status, error.config.url);
        return Promise.reject(error);
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private isCriticalAuthEndpoint(url: string | undefined): boolean {
        if (!url) return false;

        const criticalAuthEndpoints = [
            '/profile',
            '/settings',
            '/user',
            '/admin',
            // Add more critical endpoints
        ];

        return criticalAuthEndpoints.some(endpoint => url.includes(endpoint));
    }

    private getRequestIdentifier(config: AxiosRequestConfig): string {
        const { method, url, params, data } = config;
        // Create a more stable hash for the request
        return `${method}:${url}:${this.hashObject(params)}:${this.hashObject(data)}`;
    }

    private hashObject(obj: any): string {
        if (!obj) return '';
        try {
            return JSON.stringify(obj, Object.keys(obj).sort());
        } catch {
            return String(obj);
        }
    }

    public getHeaders(): { headers: any } {
        return {
            headers: {
                Authorization: `Bearer ${this.iamService.getAuthToken()}`,
            }
        };
    }

    protected toPlain(typedObject: any): object {
        return JSON.parse(JSON.stringify(typedObject));
    }

    private logout(message?: string) {
        if (message) {
            const toastOption = {
                title: 'Session Expired',
                message: message,
                dismiss: true,
                timeout: 5000,
            };
            this.uiService.showToast(message, toastOption);

            setTimeout(() => {
                this.uiService.logout();
            }, 2000);
        } else {
            this.uiService.logout();
        }
    }

    // Utility method to clear pending requests (useful for cleanup)
    public clearPendingRequests(): void {
        this.pendingRequests.clear();
    }

    // Get pending request count (useful for debugging)
    public getPendingRequestCount(): number {
        return this.pendingRequests.size;
    }
}